import React, { useMemo, useState } from "react";
import TerminalRow, {
  TerminalRecord,
} from "components/terminal-row/TerminalRow";
import SkeletonTerminalRow from "components/terminal-row/SkeletonTerminalRow";
import Button from "components/button/Button";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useTerminalLookup } from "features/terminals/useTerminalLookup";
import "./terminals-list.css";

// The on-chain lookup resolves one specific terminal, so it needs the whole serial — below this
// the merchant is still mid-type and the lookup would only ever answer "not found".
const MIN_SERIAL_LENGTH = 12;
const isFullSerial = (value: string) => value.length >= MIN_SERIAL_LENGTH;

interface Props {
  terminals: TerminalRecord[];
  loading: boolean;
  merchantId: string;
}

function TerminalsList({ terminals, loading, merchantId }: Props) {
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return terminals;
    return terminals.filter((t) => t.serial.toLowerCase().includes(q));
  }, [terminals, query]);

  const trimmedQuery = query.trim();

  // Nothing to search, and nothing the chain could add: /posBySerialForMerchant only returns a
  // terminal that belongs to THIS merchant, so a merchant holding none gets not-found every time.
  const hasNoTerminals = !loading && terminals.length === 0;

  // Only the 10 most recent terminals are loaded up front; a search miss is the cue to check
  // the chain — but only once a full serial has been entered.
  const noLocalMatch =
    !loading && trimmedQuery.length > 0 && results.length === 0;

  // Hold the request until typing settles, so we do not fire one (and flash "not found") on every
  // keystroke.
  const debouncedQuery = useDebouncedValue(trimmedQuery, 400);
  const settling = debouncedQuery !== trimmedQuery;

  const lookup = useTerminalLookup(
    debouncedQuery,
    merchantId,
    noLocalMatch && isFullSerial(trimmedQuery) && !settling,
  );

  // While the debounce catches up we are still effectively searching; once it settles the hook's
  // status drives the outcome.
  const chainStatus = settling ? "loading" : lookup.status;

  return (
    <section className="tm-list">
      <div className="tm-list__header">
        <div className="tm-list__title-wrap">
          <h3 className="tm-list__title">Terminals</h3>
          {terminals.length > 0 && (
            <span className="tm-list__count">{terminals.length}</span>
          )}
        </div>
        <input
          type="text"
          className="tm-list__search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by serial..."
          disabled={hasNoTerminals}
        />
      </div>

      {loading && terminals.length === 0 ? (
        <div className="tm-list__items">
          {Array.from({ length: 3 }, (_, i) => (
            <SkeletonTerminalRow key={i} />
          ))}
        </div>
      ) : results.length > 0 ? (
        <div className="tm-list__items">
          {results.map((t) => (
            <TerminalRow key={t.serialId} {...t} />
          ))}
        </div>
      ) : trimmedQuery.length === 0 ? (
        <div className="tm-list__empty">No terminals assigned yet.</div>
      ) : !isFullSerial(trimmedQuery) ? (
        <div className="tm-list__empty">
          No matching terminal. Enter the full serial to look it up on-chain.
        </div>
      ) : chainStatus === "loading" ? (
        <div className="tm-list__items">
          <SkeletonTerminalRow />
        </div>
      ) : chainStatus === "found" && lookup.terminal ? (
        <div className="tm-list__items">
          <p className="tm-list__note">
            Not in your recent terminals — found on-chain.
          </p>
          <TerminalRow {...lookup.terminal} />
        </div>
      ) : chainStatus === "not-found" ? (
        <div className="tm-list__empty">
          No terminal found for “{trimmedQuery}”.
        </div>
      ) : chainStatus === "error" ? (
        <div className="tm-list__error">
          <p className="tm-list__error-msg">
            Couldn’t look up this terminal right now.
          </p>
          <Button variant="ghost" size="sm" onClick={() => lookup.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        // `idle` — the gate closed under us (e.g. auth dropped). Say nothing rather than claim a
        // lookup failed that never ran.
        <div className="tm-list__empty">No terminals match your search.</div>
      )}
    </section>
  );
}

export default TerminalsList;
